import { useForm } from "@tanstack/react-form"
import { createFileRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { useState } from "react"

import { ActivityMemberPicker } from "@/components/activity-levels/member-picker"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { MetricTile } from "@/components/shared/metric-tile"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormPageSkeleton } from "@/components/ui/page-skeletons"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  useAchievementDefinitionsQuery,
  useDeleteAchievementDefinitionMutation,
  useGrantAchievementMutation,
  useRevokeAchievementMutation,
  useUpsertAchievementDefinitionMutation,
  useUserAchievementsQuery,
} from "@/hooks/activity-levels"
import {
  type AchievementDefinitionInput,
  type AchievementWire,
} from "@/lib/activity-levels-api"

export const Route = createFileRoute(
  "/_authed/_admin/activity-levels/achievements",
)({
  component: ActivityLevelsAchievementsRoute,
})

type AchievementFormValues = {
  achievementKey: string
  definition: AchievementDefinitionInput
}

const required =
  (message: string) =>
  ({ value }: { value: string }) =>
    value.trim() === "" ? message : undefined

const validCriterion = ({
  value,
}: {
  value: AchievementDefinitionInput["criterion"]
}) => {
  if (value.kind === "manual") return undefined
  return value.activityKey.trim() !== "" &&
    Number.isInteger(value.level) &&
    value.level > 0
    ? undefined
    : "Track level must be a positive whole number"
}

const firstError = (errors: unknown[]) => {
  const value = errors[0]
  if (!value) return null
  if (value instanceof Error) return value.message
  if (typeof value === "string") return value
  if (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof value.message === "string"
  )
    return value.message
  return "Something went wrong"
}

function ActivityLevelsAchievementsRoute() {
  const definitionsQuery = useAchievementDefinitionsQuery()
  const upsertDefinition = useUpsertAchievementDefinitionMutation()
  const deleteDefinition = useDeleteAchievementDefinitionMutation()

  const [editing, setEditing] = useState<AchievementWire | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AchievementWire | null>(null)

  const [selectedMemberDisplayName, setSelectedMemberDisplayName] = useState<
    string | null
  >(null)
  const [userId, setUserId] = useState<string | null>(null)

  const userAchievementsQuery = useUserAchievementsQuery(userId)
  const grant = useGrantAchievementMutation()
  const revoke = useRevokeAchievementMutation()

  if (definitionsQuery.isLoading) {
    return <FormPageSkeleton />
  }

  if (definitionsQuery.isError) {
    return (
      <StatusAlert tone="danger">
        Failed to load achievement definitions. Refresh the page or try again
        later.
      </StatusAlert>
    )
  }

  const definitions = definitionsQuery.data ?? []
  const unlockedKeys = new Set(
    (userAchievementsQuery.data ?? []).map((item) => item.achievementKey),
  )

  return (
    <div className="grid gap-4 md:gap-5">
      <PageIntroSplitHeader
        kicker={<SectionKicker>Activity Levels</SectionKicker>}
        title="Achievement Badges"
        description="Define achievement badges and grant or revoke them for members."
        metrics={
          <MetricTile label="Badges" value={String(definitions.length)} />
        }
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 size-4" />
            New badge
          </Button>
        }
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        {createOpen && (
          <AchievementForm
            mode="create"
            onClose={() => setCreateOpen(false)}
            onSubmit={(input, onSuccess) =>
              upsertDefinition.mutate(input, { onSuccess })
            }
            isPending={upsertDefinition.isPending}
          />
        )}
      </Dialog>

      <Dialog
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        {editing && (
          <AchievementForm
            mode="edit"
            achievement={editing}
            onClose={() => setEditing(null)}
            onSubmit={(input, onSuccess) =>
              upsertDefinition.mutate(input, { onSuccess })
            }
            isPending={upsertDefinition.isPending}
          />
        )}
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Badges</CardTitle>
        </CardHeader>
        <CardContent>
          {definitions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No badges yet. Create one to start awarding achievements.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Badge</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead aria-label="actions" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {definitions.map((achievement) => (
                  <TableRow key={achievement.achievementKey}>
                    <TableCell className="font-medium">
                      <span className="mr-2">{achievement.emoji}</span>
                      {achievement.name}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {achievement.achievementKey}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {achievement.description}
                    </TableCell>
                    <TableCell className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(achievement)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(achievement)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Member badges</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <ActivityMemberPicker
            id="achievement-member-search"
            onSelect={(member) => {
              setUserId(member.memberId)
              setSelectedMemberDisplayName(member.displayName)
            }}
          />
          {userId && (
            <p className="text-sm text-muted-foreground">
              Selected: {selectedMemberDisplayName ?? userId} · {userId}
            </p>
          )}

          {userId != null && (
            <div className="grid gap-2">
              {userAchievementsQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : userAchievementsQuery.isError ? (
                <StatusAlert tone="danger">
                  Failed to load member achievements.
                </StatusAlert>
              ) : definitions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No badges defined.
                </p>
              ) : (
                definitions.map((achievement) => {
                  const has = unlockedKeys.has(achievement.achievementKey)
                  return (
                    <div
                      key={achievement.achievementKey}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="text-sm">
                        <span className="mr-2">{achievement.emoji}</span>
                        {achievement.name}
                      </span>
                      <Button
                        variant={has ? "outline" : "default"}
                        size="sm"
                        disabled={grant.isPending || revoke.isPending}
                        onClick={() => {
                          if (has) {
                            revoke.mutate({
                              userId,
                              achievementKey: achievement.achievementKey,
                            })
                          } else {
                            grant.mutate({
                              userId,
                              achievementKey: achievement.achievementKey,
                            })
                          }
                        }}
                      >
                        {has ? "Revoke" : "Grant"}
                      </Button>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete badge?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the {deleteTarget?.name} badge definition and revokes
              it from all members who hold it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) {
                  deleteDefinition.mutate(deleteTarget.achievementKey)
                  setDeleteTarget(null)
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function AchievementForm({
  mode,
  achievement,
  onSubmit,
  onClose,
  isPending,
}: {
  mode: "create" | "edit"
  achievement?: AchievementWire
  onSubmit: (
    input: {
      achievementKey: string
      definition: AchievementDefinitionInput
    },
    onSuccess: () => void,
  ) => void
  onClose: () => void
  isPending: boolean
}) {
  const [invalidSubmitMessage, setInvalidSubmitMessage] = useState<
    string | null
  >(null)
  const defaultValues: AchievementFormValues = achievement
    ? {
        achievementKey: achievement.achievementKey,
        definition: {
          name: achievement.name,
          description: achievement.description,
          emoji: achievement.emoji,
          criterion: { ...achievement.criterion },
        },
      }
    : {
        achievementKey: "",
        definition: {
          name: "",
          description: "",
          emoji: "",
          criterion: { kind: "manual" },
        },
      }
  const form = useForm({
    defaultValues,
    onSubmitInvalid: () => {
      setInvalidSubmitMessage("Please fix the highlighted form errors")
    },
    onSubmit: ({ value }) => {
      setInvalidSubmitMessage(null)
      onSubmit(
        {
          achievementKey: value.achievementKey.trim(),
          definition: value.definition,
        },
        onClose,
      )
    },
  })

  return (
    <DialogContent>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "New badge" : `Edit ${achievement?.name}`}
          </DialogTitle>
        </DialogHeader>
        {invalidSubmitMessage ? (
          <p className="mt-3 text-sm text-destructive" role="alert">
            {invalidSubmitMessage}
          </p>
        ) : null}
        <div className="mt-4 grid gap-3">
          <form.Field
            name="achievementKey"
            validators={{
              onBlur: required("Achievement key is required"),
              onSubmit: required("Achievement key is required"),
            }}
          >
            {(field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = "achievement-key-error"
              return (
                <div className="grid gap-1.5">
                  <Label htmlFor="achievement-key">Achievement key</Label>
                  <Input
                    id="achievement-key"
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    disabled={mode === "edit"}
                    placeholder="first-blood"
                    required
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                  />
                  {error ? (
                    <p id={errorId} className="text-sm text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            }}
          </form.Field>
          <div className="grid grid-cols-2 gap-3">
            <form.Field
              name="definition.name"
              validators={{
                onBlur: required("Name is required"),
                onSubmit: required("Name is required"),
              }}
            >
              {(field) => {
                const error = firstError(field.state.meta.errors)
                const errorId = "achievement-name-error"
                return (
                  <div className="grid gap-1.5">
                    <Label htmlFor="achievement-name">Name</Label>
                    <Input
                      id="achievement-name"
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      required
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                    />
                    {error ? (
                      <p id={errorId} className="text-sm text-destructive">
                        {error}
                      </p>
                    ) : null}
                  </div>
                )
              }}
            </form.Field>
            <form.Field
              name="definition.emoji"
              validators={{
                onBlur: required("Emoji is required"),
                onSubmit: required("Emoji is required"),
              }}
            >
              {(field) => {
                const error = firstError(field.state.meta.errors)
                const errorId = "achievement-emoji-error"
                return (
                  <div className="grid gap-1.5">
                    <Label htmlFor="achievement-emoji">Emoji</Label>
                    <Input
                      id="achievement-emoji"
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      required
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                    />
                    {error ? (
                      <p id={errorId} className="text-sm text-destructive">
                        {error}
                      </p>
                    ) : null}
                  </div>
                )
              }}
            </form.Field>
          </div>
          <form.Field
            name="definition.description"
            validators={{
              onBlur: required("Description is required"),
              onSubmit: required("Description is required"),
            }}
          >
            {(field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = "achievement-description-error"
              return (
                <div className="grid gap-1.5">
                  <Label htmlFor="achievement-description">Description</Label>
                  <Input
                    id="achievement-description"
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    required
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                  />
                  {error ? (
                    <p id={errorId} className="text-sm text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            }}
          </form.Field>
          <form.Field
            name="definition.criterion"
            validators={{
              onChange: validCriterion,
              onBlur: validCriterion,
              onSubmit: validCriterion,
            }}
          >
            {(field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = "achievement-criterion-error"
              const trackCriterion =
                field.state.value.kind === "track-level"
                  ? field.state.value
                  : null
              return (
                <>
                  <div className="grid gap-1.5">
                    <Label htmlFor="achievement-criterion">
                      Unlock criterion
                    </Label>
                    <Select
                      value={field.state.value.kind}
                      onValueChange={(value) =>
                        field.handleChange(
                          value === "track-level"
                            ? {
                                kind: "track-level",
                                activityKey: "message",
                                level: 1,
                              }
                            : { kind: "manual" },
                        )
                      }
                    >
                      <SelectTrigger
                        id="achievement-criterion"
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? errorId : undefined}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual grant</SelectItem>
                        <SelectItem value="track-level">Track level</SelectItem>
                      </SelectContent>
                    </Select>
                    {error ? (
                      <p id={errorId} className="text-sm text-destructive">
                        {error}
                      </p>
                    ) : null}
                  </div>
                  {trackCriterion && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label htmlFor="achievement-track">Track</Label>
                        <Select value="message" disabled>
                          <SelectTrigger id="achievement-track">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="message">💬 Chat</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-1.5">
                        <Label htmlFor="achievement-level">Level</Label>
                        <Input
                          id="achievement-level"
                          type="number"
                          min={1}
                          step={1}
                          value={trackCriterion.level}
                          onBlur={field.handleBlur}
                          onChange={(event) =>
                            field.handleChange({
                              kind: "track-level",
                              activityKey: trackCriterion.activityKey,
                              level: Number(event.target.value),
                            })
                          }
                          required
                          aria-invalid={error ? true : undefined}
                          aria-describedby={error ? errorId : undefined}
                        />
                      </div>
                    </div>
                  )}
                </>
              )
            }}
          </form.Field>
        </div>
        <DialogFooter className="mt-6">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isPending} type="submit">
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
